import type { BackgroundsSizeFilter } from '@/lib/backgrounds/types/backgrounds-size-filter'
import type { BackgroundsTypeOption } from '@/lib/backgrounds/types/backgrounds-type-option'
import type { BackgroundsRequestCombo } from './types/backgrounds-request-combo'

function doublePageValuesFor(size: BackgroundsSizeFilter): boolean[] {
  if (size === 'spread') return [true]
  if (size === 'page') return [false]

  return [true, false]
}

function typeValuesFor(type: BackgroundsTypeOption): {
  fourColor: boolean[]
  backgroundType: string | null
} {
  switch (type) {
    case 'Formula colors':
      return { fourColor: [true], backgroundType: 'solid color' }
    case 'Color images':
      return { fourColor: [true], backgroundType: 'image' }
    case 'Black and white images':
      return { fourColor: [false], backgroundType: 'image' }
    case 'All backgrounds':
    default:
      return { fourColor: [true, false], backgroundType: null }
  }
}

/**
 * `doublePage` and `fourColor` are required booleans on GET /backgrounds — there is no "any"
 * value the API accepts. A Size of "Any" or a Type of "All backgrounds" therefore fans out into
 * every boolean combination needed to cover it (up to 4 when both are unconstrained at once), to
 * be fetched in parallel and merged/de-duped client-side (see `dedupeBackgroundsById`).
 */
export function resolveBackgroundsRequestCombos(
  size: BackgroundsSizeFilter,
  type: BackgroundsTypeOption
): BackgroundsRequestCombo[] {
  const doublePageValues = doublePageValuesFor(size)
  const { fourColor: fourColorValues, backgroundType } = typeValuesFor(type)

  return doublePageValues.flatMap((doublePage) =>
    fourColorValues.map((fourColor) => ({ doublePage, fourColor, backgroundType }))
  )
}
